import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import EmailRow, { EmailStatus } from "../email-row/EmailRow";
import VerifyCode from "../verify-code/VerifyCode";
import Button from "../base/Button";
import ClickableText from "../base/ClickableText";
import { useSectionForm } from "../../hooks/useSectionForm";
import { useEmailStore } from "../store/emailStore";
import Modal from "../modal/Modal";
import { EmailDefaultLocale } from "../../constants/form-fields";
import { hasError, hasMessageError, validateVerificationCode, } from "../../utils/utils";
import Spinner from "../base/Spinner";
import { useDebounceClick } from "../../hooks/useDebounceClick";
import { emailsSchema } from "../../schemas/schemas";
import { useToastWrapper } from "../../hooks/useToastWrapper";
import { APIErrorCodes, APIErrorMessages } from "../../constants/profile";
import { useLocalizedString } from "@amzn/aws-marketing-identity-kit-localizations";
import { Sanitize } from "../Sanitize";
import { useEditFocus } from "../../hooks/useFormFocus";
import { z } from "zod";
import { CountdownTimer } from "../countdown-timer/CountdownTimer";
import clsx from "clsx";
const EmailFormModal = ({ isOpen, onClose, profile, adapter, isVerifying, email, error = "", }) => {
    const [isValidating, setIsValidating] = useState(false);
    const { addEmail, updateEmailStatus, updateInitialEmails, emails } = useEmailStore();
    const [newEmail, setNewEmail] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const { control, handleSubmit, getValues, reset, setFocus } = useSectionForm(emailsSchema.superRefine((data, ctx) => {
        if (emails.some((record) => record.email === data.email)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("Email already added."),
                path: ["email"],
            });
        }
    }), "emailModal", {
        email: "",
        type: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [verificationError, setVerificationError] = useState("");
    const { debounceClick } = useDebounceClick(2000);
    const { showSuccessToast, showErrorToast } = useToastWrapper();
    const [limitReached, setLimitReached] = useState(false);
    const { t } = useLocalizedString();
    const { seconds, startCountdown, isTimerActive, resetCountdown } = CountdownTimer({});
    useEditFocus(setFocus, isOpen, "email", 100);
    const handleVerificationCall = async () => {
        const codeValidation = validateVerificationCode(verificationCode);
        const currentEmail = isVerifying ? email : newEmail;
        if (codeValidation.isDirty && !codeValidation.isValid) {
            setVerificationError(t(codeValidation.error));
            return;
        }
        setIsLoading(true);
        try {
            await adapter.VerifyEmail(currentEmail, verificationCode);
            setIsLoading(false);
            updateEmailStatus(currentEmail, { status: EmailStatus.Verified });
            updateInitialEmails();
            handleClose();
            showSuccessToast(t("Your email has been successfully verified."));
        }
        catch (error) {
            setIsLoading(false);
            if (error &&
                hasError(error, APIErrorCodes.TooManyRequests) &&
                hasMessageError(error, APIErrorMessages.TooManyRequestsPerDay)) {
                setLimitReached(true);
                return;
            }
            if (error &&
                hasError(error, APIErrorCodes.TooManyRequests) &&
                hasMessageError(error, APIErrorMessages.TooManyRequests)) {
                startCountdown();
                return;
            }
            if (error && hasError(error, APIErrorCodes.ClientError)) {
                setVerificationError(t("Please enter a valid verification code."));
                return;
            }
            handleClose();
            showErrorToast(t("There was an error try again later."));
        }
    };
    const handleEmailUpdateCall = useCallback(async (newEmail, newEmailType, profileId) => {
        try {
            const response = await adapter.AddEmail(newEmail.trim(), newEmailType);
            addEmail({
                authProvider: response?.authProvider || "",
                createdOn: 0,
                email: response?.addEmail.email,
                profileId: profileId,
                status: response.addEmail.status,
                tag: {
                    isRePostEmail: false,
                    isStartUpsEmail: false,
                    isTrainingCertificationEmail: false,
                    isUsernameEmail: false,
                },
                type: response.addEmail.type,
                updatedOn: response.addEmail.updatedOn,
                userId: profile.userId,
                verifiedOn: response.addEmail.verifiedOn,
            });
            setNewEmail(newEmail);
            setIsValidating(true);
            updateInitialEmails();
            await adapter.RequestEmailVerification(newEmail.trim(), EmailDefaultLocale);
            setIsLoading(false);
        }
        catch (error) {
            setIsLoading(false);
            if (error &&
                hasError(error, APIErrorCodes.TooManyRequests) &&
                hasMessageError(error, APIErrorMessages.TooManyRequestsPerDay)) {
                setLimitReached(true);
                return;
            }
            if (error && hasError(error, APIErrorCodes.ClientError)) {
                setVerificationError(t("Please enter a valid verification code."));
                return;
            }
            if (error &&
                hasError(error, APIErrorCodes.TooManyRequests) &&
                hasMessageError(error, APIErrorMessages.TooManyRequests)) {
                startCountdown();
                return;
            }
            showErrorToast(t("There was an error try again later."));
            handleClose();
        }
    }, [
        adapter,
        addEmail,
        profile.userId,
        setNewEmail,
        setIsValidating,
        setIsLoading,
    ]);
    const handleSave = async (data) => {
        setIsLoading(true);
        try {
            handleEmailUpdateCall(data.email, data.type, profile.id);
        }
        catch (error) {
            setIsLoading(false);
            if (error &&
                hasError(error, APIErrorCodes.TooManyRequests) &&
                hasMessageError(error, APIErrorMessages.TooManyRequestsPerDay)) {
                setLimitReached(true);
                return;
            }
            if (error && hasError(error, APIErrorCodes.ClientError)) {
                setVerificationError(t("Please enter a valid verification code."));
                return;
            }
            if (error &&
                hasError(error, APIErrorCodes.TooManyRequests) &&
                hasMessageError(error, APIErrorMessages.TooManyRequests)) {
                startCountdown();
                return;
            }
            setIsValidating(false);
        }
    };
    const handleClose = () => {
        setIsValidating(false);
        resetValues();
        setIsLoading(false);
        setLimitReached(false);
        setVerificationError("");
        resetCountdown();
        onClose();
    };
    const resetValues = () => {
        reset({ email: "", type: "" });
        setNewEmail("");
    };
    const onVerificationCodeChange = (code) => {
        setVerificationError("");
        setVerificationCode(code);
    };
    useEffect(() => {
        if (verificationCode.length === 6) {
            handleVerificationCall();
        }
    }, [verificationCode]);
    const onRequestVerificationCode = async () => {
        debounceClick(async () => {
            try {
                setVerificationError("");
                startCountdown();
                await adapter.RequestEmailVerification(isVerifying ? email.trim() : newEmail.trim(), EmailDefaultLocale);
            }
            catch (error) {
                setIsLoading(false);
                if (error &&
                    hasError(error, APIErrorCodes.TooManyRequests) &&
                    hasMessageError(error, APIErrorMessages.TooManyRequestsPerDay)) {
                    setLimitReached(true);
                    return;
                }
                if (error && hasError(error, APIErrorCodes.ClientError)) {
                    setVerificationError(t("Please enter a valid verification code."));
                    return;
                }
                if (error &&
                    hasError(error, APIErrorCodes.TooManyRequests) &&
                    hasMessageError(error, APIErrorMessages.TooManyRequests)) {
                    startCountdown();
                    return;
                }
                showErrorToast(t("There was an error sending your verification code."));
                handleClose();
            }
        });
    };
    useEffect(() => {
        if (error === APIErrorMessages.TooManyRequests) {
            startCountdown();
        }
    }, [error]);
    return (_jsxs(Modal, { isOpen: isOpen, onClose: handleClose, children: [_jsxs("div", { className: "flex flex-col w-full mt-4", children: [_jsx("h3", { className: "text-[28px] sm:text-[36px] font-bold leading-tight sm:leading-[48px] w-full mb-2 sm:mb-0", children: limitReached || error === APIErrorMessages.TooManyRequestsPerDay
                            ? t("Verification limit reached")
                            : !isValidating && !isVerifying
                                ? t("Add an email address")
                                : t("Enter verification code") }), limitReached || error === APIErrorMessages.TooManyRequestsPerDay ? (_jsx("p", { className: "text-base leading-relaxed my-4 max-w-prose", children: t("You've made the maximum number of requests allowed today. Please try again after 24 hours.") })) : ((isValidating || isVerifying) && (_jsx("p", { className: "text-base leading-relaxed my-4 w-full break-all", children: _jsx(Sanitize, { html: t('A 6-digit verification code has been sent to <span class="font-bold">{{email}}</span>. Please enter it below to confirm your email address.', {
                                email: getValues("email") || email,
                                interpolation: { escapeValue: false },
                            }) }) })))] }), limitReached || error === APIErrorMessages.TooManyRequestsPerDay ? (_jsx(_Fragment, {})) : !isValidating && !isVerifying ? (_jsx("div", { className: "pt-6 pb-6", children: _jsx(EmailRow, { isEditing: true, isVerifiying: true, buttonOptions: [], onEmailUpdate: () => { }, onTypeUpdate: () => { }, email: "", type: "", control: control, emailFieldName: "email", typeFieldName: "type", className: "items-start", isLoading: isLoading }) })) : (_jsxs("div", { className: "flex flex-col items-center  mt-[3.2rem] mb-[3.2rem]", children: [_jsx(VerifyCode, { onChange: onVerificationCodeChange, error: verificationError, disabled: isLoading }), _jsxs("p", { className: clsx("leading-relaxed my-4 text-center text-sm"), children: [_jsxs("span", { className: clsx(isTimerActive && "text-idk-disabled-text"), children: [!isTimerActive && t("Didn't receive a code?"), " "] }), _jsx("span", { className: clsx(!isTimerActive && "link cursor-pointer", isTimerActive && "text-idk-disabled-text"), tabIndex: 0, onClick: !isTimerActive ? onRequestVerificationCode : () => { }, children: isTimerActive ? (_jsx(_Fragment, { children: `${t("Request a new code")} (${seconds})` })) : (t("Request a new code.")) }), _jsx("p", { children: t("If you did not receive an email, please check your spam or junk folder.") })] })] })), _jsx("div", { className: "w-full flex justify-end items-center", children: _jsxs("div", { className: "flex items-center gap-8", children: [limitReached ||
                            error === APIErrorMessages.TooManyRequestsPerDay ||
                            isLoading ? (_jsx(_Fragment, {})) : (_jsx(ClickableText, { "data-testid": "cancelButton", onClick: handleClose, children: isVerifying || isValidating ? t("Skip") : t("Cancel") })), _jsx(Button, { "data-testid": "saveButton", variant: "primary", onClick: () => limitReached || error === APIErrorMessages.TooManyRequestsPerDay
                                ? handleClose()
                                : isVerifying || isValidating
                                    ? handleVerificationCall()
                                    : handleSubmit(handleSave), disabled: isLoading && (!limitReached || !error.length), children: limitReached ||
                                error === APIErrorMessages.TooManyRequestsPerDay ? (t("Okay")) : isLoading ? (_jsx(Spinner, {})) : isVerifying || isValidating ? (t("Submit")) : (t("Add email")) })] }) })] }));
};
export default EmailFormModal;
//# sourceMappingURL=EmailFormModal.js.map